'use client'
import React, { useMemo } from 'react'
import merge from '@/utils/nunchee-kit/merge'
import { usePaginatedPlaylist } from '@/hooks/playlists/usePaginatedPlaylist'
import { useQueryClient } from '@tanstack/react-query'
import { TopBannerCarousel } from '@/components/Carousels/TopBannerCarousel'
import { PlaylistCarousel } from '@/components/Carousels/PlaylistCarousel'
import { WSCCarousel } from '@/components/Carousels/WSCCarousel'
import { useIntersectionObserver } from '@uidotdev/usehooks'

import { IPlaylistScript } from '@/types'
import { PLAYLIST_TYPES } from '@/constants'
import { IPlaylistResponse } from '@/types/playlist'

import { TOP_BANNER_DEFAULTS, HORIZONTAL_DEFAULTS, EVENTS_DEFAULTS } from './defaults'
import classNames from 'classnames'
import classes from './styles.module.scss'

interface PlaylistProps {
  playlistScript: IPlaylistScript
}

const WSC_TYPE = {
  [PLAYLIST_TYPES.WSC_STORIES]: 'story',
  [PLAYLIST_TYPES.WSC_MOMENTS]: 'moment',
} as const

const PRELOAD_ROOT_MARGIN = '800px'

export const Playlist = (props: PlaylistProps) => {
  const { playlistScript } = props
  const { options, type } = playlistScript
  const isTopBanner = type === PLAYLIST_TYPES.TOP_BANNER
  const isHorizontal = type === PLAYLIST_TYPES.HORIZONTAL
  const isEvents = type === PLAYLIST_TYPES.EVENTS
  const isWscStories = type === PLAYLIST_TYPES.WSC_STORIES
  const isWscMoments = type === PLAYLIST_TYPES.WSC_MOMENTS
  const isWscPlaylist = isWscStories || isWscMoments

  const playlistOptions = useMemo(() => merge({},
    isTopBanner ? TOP_BANNER_DEFAULTS :
      isEvents ? EVENTS_DEFAULTS :
        HORIZONTAL_DEFAULTS, options
  ), [options, isTopBanner, isEvents])

  const playlistBackgroundImage = useMemo(() => options?.background?.imageUrl, [options])
  const playlistBackgroundColor = useMemo(() => options?.background?.color, [options])

  const queryClient = useQueryClient()
  const hasCachedData = !!queryClient.getQueryData(['playlist', playlistScript._id, playlistOptions?.params])

  const [ref, entry] = useIntersectionObserver({
    threshold: 0,
    root: null,
    rootMargin: PRELOAD_ROOT_MARGIN,
  })

  // FZ-WEB-NEXTJS-21: Track viewport visibility via IntersectionObserver so that
  // refetchOnWindowFocus is only enabled for playlists currently visible in the
  // viewport — preventing the N+1 burst of simultaneous refetches when the user
  // switches tabs and all playlist components fire at once.
  const isVisible = !!entry?.isIntersecting

  const { items, playlistData, fetchNextPage, hasNextPage, isFetching, isPlaceholderData } = usePaginatedPlaylist({
    id: playlistScript?._id,
    enabled: isVisible || !playlistOptions?.hasLazyLoader || hasCachedData,
    isVisible,
    options: { ...playlistOptions, placeholderItems: isTopBanner ? 1 : 8 },
  })

  const hasItems = items?.length > 0

  if (playlistOptions?.hideEmptyLists && !hasItems && !isWscPlaylist) return null

  if (isTopBanner) {
    return (
      <div
        ref={ref}
        className={classNames({
          [classes.container]: true,
          [classes['top-banner-playlist']]: true
        })}
      >
        <TopBannerCarousel
          items={items}
          options={playlistOptions}
          playlistScript={playlistScript}
          isPlaceholder={isPlaceholderData}
        />
      </div>
    )
  }

  if (isWscPlaylist) {
    return (
      <div
        ref={ref}
        className={classNames({
          [classes.container]: true,
          [classes['wsc-playlist']]: true
        })}
      >
        <WSCCarousel
          type={WSC_TYPE[type]}
          playlistData={playlistData as any}
          playlistScript={playlistScript}
          isPlaceholder={isPlaceholderData}
        />
      </div>
    )
  }

  if (isEvents) {
    return (
      <div
        className={classNames({
          [classes.container]: true,
          [classes['events-playlist']]: isEvents,
        })}
      >
        <div
          ref={ref}
          className={classes.slider}
        >
          <PlaylistCarousel
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetching={isFetching}
            isPlaceholder={isPlaceholderData}
            items={items}
            playlistData={playlistData as IPlaylistResponse}
            playlistOptions={playlistOptions}
            playlistScript={playlistScript}
            playlistType={type}
          />
        </div>
      </div>
    )
  }

  return (
    <div
      className={classNames({
        [classes.container]: true,
        [classes.background]: !!playlistBackgroundImage,
        [classes['horizontal-playlist']]: isHorizontal,
      })}
      style={{
        backgroundImage: (playlistBackgroundImage && !isPlaceholderData) ? `url(${playlistBackgroundImage})` : undefined,
        backgroundColor: (playlistBackgroundColor && !isPlaceholderData) ? playlistBackgroundColor : undefined,
        backgroundPosition: 'left',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div
        ref={ref}
        className={classes.slider}
      >
        <PlaylistCarousel
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetching={isFetching}
          isPlaceholder={isPlaceholderData}
          items={items}
          playlistData={playlistData as IPlaylistResponse}
          playlistOptions={playlistOptions}
          playlistScript={playlistScript}
          playlistType={type}
        />
      </div>
    </div>
  )
}
